import { useState, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { MobileTabBar } from './MobileTabBar';

// LocalStorage key for sidebar state (shared with Sidebar component)
const SIDEBAR_COLLAPSED_KEY = 'filmple:sidebar_collapsed';

// Routes that should NOT show the sidebar/topbar layout
const FULL_PAGE_ROUTES = ['/auth/callback', '/reset-password'];

// Routes that show sidebar but hide topbar's Movies/TV toggle
// (toggle only shows on discover page)
// This is handled in TopBar component itself

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();

  // Track sidebar collapse state to adjust content margin
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Listen for sidebar state changes via custom event (for immediate sync)
  useEffect(() => {
    const handleSidebarChange = () => {
      try {
        const collapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
        setSidebarCollapsed(collapsed);
      } catch {
        // Ignore errors
      }
    };

    // Listen for custom event dispatched by Sidebar when it changes state
    window.addEventListener('sidebar-collapse-change', handleSidebarChange);
    return () => window.removeEventListener('sidebar-collapse-change', handleSidebarChange);
  }, []);

  // Check if current route should show full-page layout (no sidebar/topbar)
  const isFullPageRoute = FULL_PAGE_ROUTES.some((route) => location.pathname.startsWith(route));

  // Inbox route uses WhatsApp Web scroll model (no page scroll, only pane scrolls)
  const isInboxRoute = location.pathname.startsWith('/inbox');

  // For full-page routes, just render children without layout
  if (isFullPageRoute) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* TopBar */}
      <TopBar sidebarCollapsed={sidebarCollapsed} />

      {/* Main Content */}
      <main
        className={`pt-16 transition-all duration-200 ${
          sidebarCollapsed ? 'md:ml-[72px]' : 'md:ml-[260px]'
        } ml-0 pb-16 md:pb-0 ${
          isInboxRoute ? 'h-screen overflow-hidden flex flex-col' : 'min-h-screen'
        }`}
      >
        <div className={`${isInboxRoute ? 'flex-1 min-h-0' : ''} animate-in fade-in duration-200`}>
          {children}
        </div>
      </main>

      {/* Mobile Tab Bar - shown on mobile only */}
      <MobileTabBar />
    </div>
  );
}
